import { format, isValid, parseISO } from 'date-fns';

export const getIsDateThisYear = (date: Date) => date.getFullYear() === new Date().getFullYear();

export const formatDateString = ({
    date,
    dateFormatString,
    removeTime = true,
    timeFormat: TimeFormatString,
}: {
    date: string | Date | null | undefined;
    dateFormatString?: string;
    removeTime?: boolean;
    timeFormat?: string;
}) => {
    if (date == null) return '';

    const parsedDate = typeof date === 'string' ? parseISO(date) : date;
    if (parsedDate == null || !isValid(parsedDate)) return '';

    const timeFormat = removeTime ? '' : ` ${TimeFormatString}`;

    return date ? format(parsedDate, `${dateFormatString}${timeFormat}`) : '';
};

export const formatDateStringIgnoreTime = ({
    date,
    dateFormatString,
}: {
    date: string | Date | null | undefined;
    dateFormatString?: string;
}) =>
    formatDateString({
        date: typeof date === 'string' ? isoDateNoTimeDiff(date) : date,
        dateFormatString,
    });

/**
 * Extract the date string portion of an ISO8601 datetime string without the time difference portion.
 * Basically ignore everything from the `T` and beyond.
 * @param dateString
 */
export const isoDateStringNoTimeDiff = (dateString: string | null | undefined) => {
    if (dateString == null) {
        return '';
    }

    const isoDate = parseISO(dateString);

    if (!isValid(isoDate)) {
        return '';
    }

    // expected ISO8601 date 2022-01-01T00:00:00+00:00
    const parts = dateString.split('T');

    return parts.length === 2 ? parts[0] : '';
};

/**
 * @see isoDateStringNoTimeDiff
 * @param dateString
 */
export const isoDateNoTimeDiff = (dateString: string) => {
    const noTimeString = isoDateStringNoTimeDiff(dateString);
    if (dateString == '') {
        return null;
    }

    // parseISO reads a date-only string as local midnight; `new Date('yyyy-MM-dd')` is UTC
    // midnight, which is the previous day for anyone west of UTC.
    return noTimeString === '' ? null : parseISO(noTimeString);
};

export const formatDateToMonthYear = (dateString: string): string => {
    const date = parseISO(dateString);
    return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(date);
};

export const formatLocaleDate = (date: string | Date, locale?: string): string => {
    const parsed = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(parsed)) return '';

    const resolvedLocale = locale ?? navigator?.language ?? 'en-GB';

    const options: Intl.DateTimeFormatOptions = {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
    };

    return new Intl.DateTimeFormat(resolvedLocale, options).format(parsed);
};
